import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import { dsClass } from '../../../../common/constants/dsClasses';
import { showMoreMsg, t } from '../../../../common/i18n';
import { Loading } from '../../../Loading';
import type { PickupPoint } from '../../../../generated/api/pickupPoint';
import type { DeliveryLogo } from '../../placeOrderTypes';

interface PickupPointListProps {
  points: PickupPoint[];
  selectedPointId?: string;
  displayedCount: number;
  initialFetchCount: number;
  isLoading: boolean;
  logo: DeliveryLogo;
  onSelect: (pointId: string) => void;
  onClear: () => void;
  onLoadMore: () => void;
}

export const PickupPointList = ({
  points,
  selectedPointId,
  displayedCount,
  initialFetchCount,
  isLoading,
  logo,
  onSelect,
  onClear,
  onLoadMore,
}: PickupPointListProps) => {
  if (!points.length && isLoading) {
    return <Loading />;
  }

  if (!points.length) {
    return null;
  }

  const visiblePoints = points
    .filter(point => !selectedPointId || point.id === selectedPointId)
    .slice(0, selectedPointId ? undefined : displayedCount);

  const showLoadMore = !selectedPointId && (displayedCount < points.length || points.length >= initialFetchCount);

  return (
    <div className={`of-place-order-delivery-method__pickup-points ${dsClass.MARGIN_TOP_3}`}>
      <div className="of-place-order-delivery-method__pickup-points-title">{t.MZ1U('Select pickup point')}</div>
      <div className="of-place-order-delivery-method__pickup-points-list">
        {visiblePoints.map(point => {
          const isSelected = selectedPointId === point.id;
          return (
            <div
              key={point.id}
              className={classNames('of-place-order-delivery-method__pickup-point', { selected: isSelected })}
            >
              <div className="of-place-order-delivery-method__pickup-point-content">
                <div className={`of-place-order-delivery-method__pickup-point-logo ${logo}`} />
                <div className="of-place-order-delivery-method__pickup-point-info">
                  <div className="of-place-order-delivery-method__pickup-point-name">{point.name}</div>
                  <div className="of-place-order-delivery-method__pickup-point-address">
                    {point.address}, {point.postalCode}
                  </div>
                </div>
                {isSelected ? (
                  <CL.Button type="button" onClick={onClear} color="tertiary">
                    {t.IUX3('Clear selection')}
                    <CL.Icon icon="close" color="primary" type="filled" className={dsClass.MARGIN_LEFT_2} />
                  </CL.Button>
                ) : (
                  <CL.Button type="button" onClick={() => onSelect(point.id)} color="tertiary">
                    {t.QRYV('Select')}
                  </CL.Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {showLoadMore && (
        <CL.Button
          type="button"
          className={dsClass.MARGIN_TOP_3}
          onClick={onLoadMore}
          color="secondary"
          loading={isLoading}
        >
          {t.H6OA(showMoreMsg)}
        </CL.Button>
      )}
    </div>
  );
};
